// import React, { forwardRef } from "react";
// import TextField from "@mui/material/TextField";
// import { styled } from "@mui/material/styles";
// import { InputLabel } from "@mui/material";

// const StyledTextField = styled(TextField)(
//   ({
//     width,
//     normalcolor,
//     activecolor,
//     disabledcolor,
//     errorcolor,
//     error,
//     helperTextColor,
//   }) => ({
//     width: width || "100%", // Default width to 100%
//     "& .MuiOutlinedInput-root": {
//       "& fieldset": {
//         borderColor: error ? errorcolor : normalcolor || "#B0B0B0", // Normal or Error state color
//       },
//       "&:hover fieldset": {
//         borderColor: error ? errorcolor : normalcolor || "#B0B0B0",
//       },
//       "&.Mui-focused fieldset": {
//         borderColor: error ? errorcolor : activecolor || "#418CFF", // Active or Error state color
//       },
//       "&.Mui-disabled fieldset": {
//         borderColor: disabledcolor || "#D3D3D3", // Disabled state color
//       },
//     },
//     "& .MuiInputLabel-root": {
//       color: error ? errorcolor : "#666", // title color in normal or error state
//     },
//     "& .MuiFormHelperText-root": {
//       color: helperTextColor || errorcolor || "#ff5555", // Error message color
//     },
//   })
// );

// const CustomInput = forwardRef(
//   (
//     {
//       type,
//       title,
//       placeholder,
//       width,
//       normalcolor,
//       activecolor,
//       disabledcolor,
//       errorcolor = "#ff5555",
//       error = false,
//       errorMessage,
//       customcolor,
//       fontWeight,
//       max,
//       helperTextColor = "#ff5555", // Accept `max` prop from parent component
//       ...props
//     },
//     ref
//   ) => {
//     return (
//       <div>
//         {title && (
//           <InputLabel
//             sx={{ color: error ? errorcolor : "#36383A", marginBottom: "8px" }}
//           >
//             {title}
//           </InputLabel>
//         )}
//         <StyledTextField
//           type={type}
//           placeholder={placeholder || "Placeholder"}
//           variant="outlined"
//           fullWidth
//           width={width}
//           normalcolor={normalcolor}
//           activecolor={activecolor}
//           disabledcolor={disabledcolor}
//           errorcolor={errorcolor}
//           error={error} // Pass the error state
//           helperText={error ? errorMessage : ""} // Display error message if error is true
//           inputRef={ref} // Use inputRef for the actual input element
//           helperTextColor={helperTextColor}
//           {...(type === "date" && { inputProps: { max } })} // Set max date for date input field using the prop
//           {...props}
//         />
//       </div>
//     );
//   }
// );

// export default CustomInput;

import React, { forwardRef, useState } from "react";
import TextField from "@mui/material/TextField";
import { styled } from "@mui/material/styles";
import { InputLabel } from "@mui/material";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { IconButton, InputAdornment } from "@mui/material";

const StyledTextField = styled(TextField)(
  ({
    width,
    normalcolor,
    activecolor,
    disabledcolor,
    errorcolor,
    error,
    helperTextColor,
  }) => ({
    width: width || "100%",
    "& .MuiOutlinedInput-root": {
      "& fieldset": {
        borderColor: error ? errorcolor || "#ff5555" : normalcolor || "#B0B0B0",
      },
      "&:hover fieldset": {
        borderColor: error ? errorcolor || "#ff5555" : normalcolor || "#B0B0B0",
      },
      "&.Mui-focused fieldset": {
        borderColor: error ? errorcolor || "#ff5555" : activecolor || "#418CFF",
      },
      "&.Mui-disabled fieldset": {
        borderColor: disabledcolor || "#D3D3D3",
      },
    },

    // 👇 force override when MUI sets .Mui-error
    "& .MuiOutlinedInput-root.Mui-error fieldset": {
      borderColor: errorcolor || "#ff5555 !important",
    },
    "& .MuiInputLabel-root.Mui-error": {
      color: errorcolor || "#ff5555 !important",
    },
    "& .MuiFormHelperText-root.Mui-error": {
      color: helperTextColor || errorcolor || "#ff5555 !important",
    },
  }),
);

const CustomInput = forwardRef(
  (
    {
      type,
      title,
      placeholder,
      width,
      normalcolor,
      activecolor,
      disabledcolor,
      errorcolor = "#ff5555",
      error = false,
      errorMessage,
      helperTextColor = "#ff5555",
      customcolor,
      fontWeight,
      max,
      allowDecimal = true,
      InputProps = {},
      onEnter,
      ...props
    },
    ref,
  ) => {
    const isPassword = type === "password";
    const [showPassword, setShowPassword] = useState(false);

    // Block e, E, +, - and optionally .
    const handleKeyDown = (e) => {
      if (type === "number") {
        // For number inputs, block e, E, +, -
        const invalidChars = ["e", "E", "+", "-"];
        if (invalidChars.includes(e.key)) {
          e.preventDefault();
        }
        if (!allowDecimal && e.key === ".") {
          e.preventDefault();
        }
      } else if (type === "email") {
        // For email inputs, allow alphanumeric, @, ., _, -, and control keys
        if (
          !/^[a-zA-Z0-9@._-]$/.test(e.key) &&
          !["Backspace", "Delete", "ArrowLeft", "ArrowRight", "Tab"].includes(
            e.key,
          )
        ) {
          e.preventDefault();
        }

        // Trigger onEnter when Enter is pressed
        if (e.key === "Enter" && onEnter) {
          onEnter();
        }
      }
    };

    const handlePaste = (e) => {
      const pastedText = e.clipboardData.getData("Text");

      if (type === "number") {
        // For number inputs
        if (/[eE+-]/.test(pastedText)) {
          e.preventDefault();
        }
        if (!allowDecimal && pastedText.includes(".")) {
          e.preventDefault();
        }
      } else if (type === "email") {
        // For email inputs, validate pasted text
        const emailRegex = /^[a-zA-Z0-9@._-]+$/;
        if (!emailRegex.test(pastedText)) {
          e.preventDefault();
        }
      }
    };

    const mergedInputProps = {
      ...(InputProps?.inputProps || {}),
      ...(type === "date" && max ? { max } : {}),
    };

    return (
      <div>
        {title && (
          <InputLabel
            sx={{ color: error ? errorcolor : "#36383A", marginBottom: "8px" }}
          >
            {title}
          </InputLabel>
        )}
        <StyledTextField
          type={
            isPassword && !showPassword
              ? "password"
              : showPassword && isPassword
                ? "text"
                : type
          }
          placeholder={placeholder || "Placeholder"}
          variant="outlined"
          fullWidth={!width}
          width={width}
          normalcolor={normalcolor}
          activecolor={activecolor}
          disabledcolor={disabledcolor}
          errorcolor={errorcolor}
          error={error}
          helperText={errorMessage || ""}
          inputRef={ref}
          helperTextColor={helperTextColor}
          onWheel={(e) => e.target.blur()}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
          InputProps={{
            ...InputProps,
            inputProps: mergedInputProps,
            endAdornment: isPassword ? (
              <InputAdornment position="end">
                <IconButton
                  onClick={() => setShowPassword((prev) => !prev)}
                  edge="end"
                  tabIndex={-1}
                >
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ) : (
              InputProps?.endAdornment
            ),
          }}
          {...props}
        />
      </div>
    );
  },
);

export default CustomInput;
